import React, { useMemo } from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import assetsData from '../../data/assets-index.json'

/**
 * GatsbyMediaByName 组件
 * 根据 name 参数从 Contentful Asset 获取文件并渲染 GatsbyImage 或视频
 * 所有媒体相关的数据都从构建时生成的 JSON 文件中获取
 *
 * @param {Object} props - 组件属性
 * @param {string} props.name - Contentful Asset 的 title 字段（用于匹配）
 * @param {string} props.className - 额外的 CSS 类名
 * @param {string} props.alt - 图片的 alt 文本（可选，默认使用 asset 的 description 或 title）
 * @param {boolean} props.autoPlay - 视频是否自动播放（默认 false）
 * @param {boolean} props.loop - 视频是否循环播放（默认 false）
 * @param {boolean} props.muted - 视频是否静音（默认 false）
 * @param {boolean} props.controls - 视频是否显示控制条（默认 true）
 * @returns {React.ReactElement|null} 渲染的 GatsbyImage 或 video 组件或 null
 */
const GatsbyMediaByName = ({
  name,
  className = '',
  alt,
  autoPlay = true,
  loop = true,
  muted = false,
  controls = true,
}) => {
  // 根据 name（匹配 title）查找对应的 Asset
  // 使用 useMemo 确保在服务端和客户端使用相同的逻辑
  const assetData = useMemo(() => {
    if (!name || !assetsData || !Array.isArray(assetsData)) {
      return null
    }

    const foundAsset = assetsData.find((asset) => asset.title === name)

    if (!foundAsset) {
      return null
    }

    // 确保 file 对象存在且是有效的对象
    const file =
      foundAsset.file && typeof foundAsset.file === 'object'
        ? foundAsset.file
        : null

    return {
      gatsbyImageData: foundAsset.gatsbyImageData || null,
      file,
      alt: alt || foundAsset.description || foundAsset.title || '',
      // 预先计算是否为视频，确保在服务端和客户端一致
      isVideo:
        file?.contentType?.startsWith('video/') ||
        (!foundAsset.gatsbyImageData && file?.url),
      fileUrl: file?.url || null,
    }
  }, [name, alt])

  if (!assetData) {
    return null
  }

  // 检查是否为视频资源
  // 使用预先计算的值，确保在服务端和客户端一致
  const isVideo = assetData.isVideo

  // 如果是视频资源
  if (isVideo && assetData.fileUrl) {
    const videoUrl = assetData.fileUrl.startsWith('//')
      ? `https:${assetData.fileUrl}`
      : assetData.fileUrl

    return (
      <video
        src={videoUrl}
        controls={controls}
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        playsInline
        className={`w-full h-full object-contain ${className}`.trim()}
        style={{
          objectFit: 'contain',
          width: '100%',
          height: '100%',
        }}
        suppressHydrationWarning
      >
        <track kind="captions" />
      </video>
    )
  }

  // 如果是图片资源
  if (assetData.gatsbyImageData) {
    return (
      <GatsbyImage
        image={assetData.gatsbyImageData}
        alt={assetData.alt}
        className={`w-full h-full object-contain ${className}`.trim()}
        imgStyle={{
          objectFit: 'contain',
          width: '100%',
          height: '100%',
        }}
      />
    )
  }

  // 如果既不是视频也不是图片，返回 null
  return null
}

export default GatsbyMediaByName
